/**
 * Label Placement Utility
 *
 * Computes a default label position (a fraction in 0..1 along an edge's path)
 * that staggers labels belonging to the same parallel group (edges connecting
 * the same node pair). Without this, every parallel edge defaults its label to
 * the path midpoint (0.5), causing labels to stack directly on top of each
 * other and become unreadable.
 *
 * This is a pure, behavior-preserving helper:
 *  - A solitary edge (groupSize <= 1) stays centered at ~0.5.
 *  - Edges in a parallel group are spread across a band roughly 0.3..0.7 so
 *    each label sits at a distinct fraction along its own path.
 *
 * It does NOT mutate any edge and only produces a number; callers decide
 * whether to apply it (e.g. only when the edge has no explicit labelPosition).
 */

import type { DiagramEdge } from '../types/schema'

/** Lower bound of the staggered band. */
export const LABEL_BAND_MIN = 0.3
/** Upper bound of the staggered band. */
export const LABEL_BAND_MAX = 0.7
/** Position used for a solitary edge (no parallel siblings). */
export const LABEL_CENTER = 0.5

/**
 * Compute a staggered label position for an edge given its index within its
 * parallel group and the total size of that group.
 *
 * @param index     Zero-based index of the edge within its parallel group.
 * @param groupSize Number of edges in the parallel group (>= 1).
 * @returns A fraction along the path. Solitary edges return ~0.5; grouped edges
 *          are spread evenly across LABEL_BAND_MIN..LABEL_BAND_MAX.
 */
export function getStaggeredLabelPosition(index: number, groupSize: number): number {
  // Solitary edge (or malformed group): keep centered.
  if (!Number.isFinite(groupSize) || groupSize <= 1) {
    return LABEL_CENTER
  }

  const safeIndex = Number.isFinite(index) ? Math.max(0, Math.min(index, groupSize - 1)) : 0

  // Evenly distribute group members across the band. For groupSize === 2 the
  // step lands the two labels at the band edges (0.3, 0.7); larger groups fill
  // the interior as well (e.g. 3 -> 0.3, 0.5, 0.7).
  const step = (LABEL_BAND_MAX - LABEL_BAND_MIN) / (groupSize - 1)
  const position = LABEL_BAND_MIN + safeIndex * step

  return clamp01(position)
}

/**
 * Unordered node-pair key so A->B and B->A fall in the same parallel group.
 */
function parallelGroupKey(source: string, target: string): string {
  return source < target ? `${source}|${target}` : `${target}|${source}`
}

/**
 * Assign a default staggered `data.labelPosition` to edges that don't already
 * specify one. Edges are grouped by their unordered node pair; within each
 * group, labels are staggered across LABEL_BAND_MIN..LABEL_BAND_MAX.
 *
 * Behavior-preserving guarantees:
 *  - Edges that already have a defined `data.labelPosition` are never changed.
 *  - Solitary edges receive ~0.5 (the existing default), so single-edge
 *    diagrams render identically.
 *  - Returns a new array of new edge objects (shallow-cloned data); inputs are
 *    not mutated.
 */
export function assignStaggeredLabelPositions(edges: DiagramEdge[]): DiagramEdge[] {
  if (!edges || edges.length === 0) {
    return edges
  }

  // Build parallel groups by unordered node pair, preserving edge order.
  const groups = new Map<string, DiagramEdge[]>()
  edges.forEach((edge) => {
    if (!edge.source || !edge.target) {
      return
    }
    const key = parallelGroupKey(edge.source, edge.target)
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key)!.push(edge)
  })

  // Map each edge to its index within its parallel group and group size.
  const placement = new Map<DiagramEdge, { index: number; size: number }>()
  groups.forEach((groupEdges) => {
    groupEdges.forEach((edge, index) => {
      placement.set(edge, { index, size: groupEdges.length })
    })
  })

  return edges.map((edge) => {
    const explicit = edge.data?.labelPosition
    if (typeof explicit === 'number' && Number.isFinite(explicit)) {
      // Respect an existing explicit position.
      return edge
    }

    const info = placement.get(edge)
    if (!info || info.size <= 1) {
      // Solitary edge: leave it to fall back to the 0.5 default downstream.
      return edge
    }

    const labelPosition = getStaggeredLabelPosition(info.index, info.size)
    return {
      ...edge,
      data: {
        ...edge.data,
        labelPosition,
      },
    }
  })
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) {
    return LABEL_CENTER
  }
  return Math.max(0, Math.min(1, value))
}
